/**
 * STRIP DOWN MUI-LAB-ALERT when it moves to '@material-ui/core' replace this w it
 */
import Paper from '@material-ui/core/Paper';
import {
  darken,
  lighten,
  withStyles,
  WithStyles,
  createStyles
} from '@material-ui/core/styles';
import { capitalize } from '@material-ui/core/utils';
import clsx from 'clsx';
import React from 'react';
import { orange, blue, green } from '@material-ui/core/colors';

export type Color = 'success' | 'info' | 'warning' | 'error';
export type AlertClassKey =
  | 'root'
  | 'standardSuccess'
  | 'standardInfo'
  | 'standardWarning'
  | 'standardError'
  | 'outlinedSuccess'
  | 'outlinedInfo'
  | 'outlinedWarning'
  | 'outlinedError'
  | 'filledSuccess'
  | 'filledInfo'
  | 'filledWarning'
  | 'filledError'
  | 'message';

export type AlertProps = {
  color?: Color;
  severity?: Color;
  role?: string;
  variant?: 'standard' | 'filled' | 'outlined';
  align?: 'center' | 'left';
  className?: string;
};

const Alert = withStyles<AlertClassKey, {}, AlertProps>(
  theme => {
    const getColor = theme.palette.type === 'light' ? darken : lighten;
    const getBackgroundColor =
      theme.palette.type === 'light' ? lighten : darken;

    return createStyles({
      /* Styles applied to the root element. */
      root: {
        ...theme.typography.body2,
        // borderRadius: theme.shape.borderRadius,
        backgroundColor: 'transparent',
        display: 'flex',
        padding: '6px 16px'
      },
      alignLeft: {
        justifyContent: 'left'
      },
      alignCenter: {
        justifyContent: 'center',
        '&>div': {
          textAlign: 'center'
        }
      },
      /* Styles applied to the root element if `variant="standard"` and `color="success"`. */
      standardSuccess: {
        color: getColor(green[500], 0.6),
        backgroundColor: getBackgroundColor(green[500], 0.9)
      },
      /* Styles applied to the root element if `variant="standard"` and `color="info"`. */
      standardInfo: {
        color: getColor(blue[500], 0.6),
        backgroundColor: getBackgroundColor(blue[500], 0.9)
      },
      /* Styles applied to the root element if `variant="standard"` and `color="warning"`. */
      standardWarning: {
        color: getColor(orange[500], 0.6),
        backgroundColor: getBackgroundColor(orange[500], 0.9)
      },
      /* Styles applied to the root element if `variant="standard"` and `color="error"`. */
      standardError: {
        color: getColor(theme.palette.error.main, 0.6),
        backgroundColor: getBackgroundColor(theme.palette.error.main, 0.9)
      },
      /* Styles applied to the root element if `variant="outlined"` and `color="success"`. */
      outlinedSuccess: {
        color: getColor(green[500], 0.6),
        border: `1px solid ${green[500]}`
      },
      /* Styles applied to the root element if `variant="outlined"` and `color="info"`. */
      outlinedInfo: {
        color: getColor(blue[500], 0.6),
        border: `1px solid ${blue[500]}`
      },
      /* Styles applied to the root element if `variant="outlined"` and `color="warning"`. */
      outlinedWarning: {
        color: getColor(orange[500], 0.6),
        border: `1px solid ${orange[500]}`
      },
      /* Styles applied to the root element if `variant="outlined"` and `color="error"`. */
      outlinedError: {
        color: getColor(theme.palette.error.main, 0.6),
        border: `1px solid ${theme.palette.error.main}`
      },
      /* Styles applied to the root element if `variant="filled"` and `color="success"`. */
      filledSuccess: {
        color: '#fff',
        fontWeight: theme.typography.fontWeightMedium,
        backgroundColor: green[500]
      },
      /* Styles applied to the root element if `variant="filled"` and `color="info"`. */
      filledInfo: {
        color: '#fff',
        fontWeight: theme.typography.fontWeightMedium,
        backgroundColor: blue[500]
      },
      /* Styles applied to the root element if `variant="filled"` and `color="warning"`. */
      filledWarning: {
        color: '#fff',
        fontWeight: theme.typography.fontWeightMedium,
        backgroundColor: orange[500]
      },
      /* Styles applied to the root element if `variant="filled"` and `color="error"`. */
      filledError: {
        color: '#fff',
        fontWeight: theme.typography.fontWeightMedium,
        backgroundColor: theme.palette.error.main
      },
      /* Styles applied to the message wrapper element. */
      message: {
        padding: '8px 0'
      }
    });
  },
  { name: 'FdAlert' }
)(
  React.forwardRef<{}, WithStyles<AlertClassKey> & AlertProps>(function Alert(
    props,
    ref
  ) {
    const {
      children,
      classes,
      className,
      color,
      role = 'alert',
      severity = 'success',
      variant = 'standard',
      align = 'left',
      ...other
    } = props;

    return (
      <Paper
        role={role}
        square
        elevation={0}
        className={clsx(
          classes.root,
          classes[`${variant}${capitalize(color || severity)}`],
          classes[`align${capitalize(align)}`],
          className
        )}
        ref={ref}
        {...other}
      >
        <div className={classes.message}>{children}</div>
      </Paper>
    );
  })
);

export default Alert;
